/**
 * Toggle Save Ritualist Hook
 * Handles save/unsave operations for ritualists with proper cache invalidation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toggleSaveRitualist } from "../services/community.service"

interface UseToggleSaveRitualistParams {
  communityId: string
}

/**
 * Toggle Save Ritualist Mutation Hook
 * Saves or unsaves a ritualist and handles:
 * - Cache invalidation for community ritualists list
 * - Optimistic UI update for immediate feedback
 */
export function useToggleSaveRitualist({ communityId }: UseToggleSaveRitualistParams) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ritualistId: string) => toggleSaveRitualist(ritualistId),
    onMutate: async ritualistId => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["communityRitualists", communityId],
      })

      // Snapshot all query variants for rollback (handles highlighted/searchQuery permutations)
      const previousRitualists = queryClient.getQueriesData({
        queryKey: ["communityRitualists", communityId],
      })

      // Optimistically update the isSaved status across all variants
      queryClient.setQueriesData(
        { queryKey: ["communityRitualists", communityId] },
        (old: unknown) => {
          if (!old || typeof old !== "object") return old

          const data = old as {
            pages?: Array<{ members?: Array<{ id: string; isSaved?: boolean }> }>
          }
          if (!data.pages) return old

          return {
            ...data,
            pages: data.pages.map(page => ({
              ...page,
              members: page.members?.map(member =>
                member.id === ritualistId ? { ...member, isSaved: !member.isSaved } : member,
              ),
            })),
          }
        },
      )

      return { previousRitualists }
    },
    onError: (_err, _ritualistId, context) => {
      // Rollback all query variants on error
      if (context?.previousRitualists) {
        for (const [key, data] of context.previousRitualists) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      // Refetch to ensure data is fresh
      queryClient.invalidateQueries({
        queryKey: ["communityRitualists", communityId],
      })
    },
  })
}
